import React, { useState, useEffect } from 'react';

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ first_name: '', last_name: '', email: '', level: 'invite', role: 'aucun_role' });
  const [createResult, setCreateResult] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const response = await fetch('/api/v1/admin/dashboard', {
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });

      const responseData = await response.json();

      if (response.ok) {
        setData(responseData.data);
      } else {
        setError(responseData.message || 'Erreur lors du chargement');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };


  const handleCreateMember = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateResult(null);
    try {
      const res = await fetch('/api/v1/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify(createForm)
      });
      const data = await res.json();
      setCreateResult(data);
      if (res.ok && data.success !== false) {
        setCreateForm({ first_name: '', last_name: '', email: '', level: 'invite', role: 'aucun_role' });
        fetchAdminData();
      }
    } catch {
      setCreateResult({ success: false, message: 'Erreur réseau' });
    } finally {
      setCreateLoading(false);
    }
  };

  const handleSponsorshipAction = async (sponsorshipId, action) => {
    try {
      const response = await fetch(`/api/v1/admin/sponsorships/${sponsorshipId}/${action}`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });

      const responseData = await response.json();

      if (response.ok) {
        // Recharger les données
        fetchAdminData();
      } else {
        alert(responseData.message || 'Erreur lors de l\'action');
      }
    } catch (err) {
      alert('Erreur de connexion');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'bg-warning text-dark', text: 'En attente' },
      approved: { class: 'bg-success', text: 'Approuvé' },
      completed: { class: 'bg-primary', text: 'Complété' },
      rejected: { class: 'bg-danger', text: 'Rejeté' }
    };

    const badge = badges[status] || { class: 'bg-secondary', text: status };
    
    return (
      <span className={`badge ${badge.class}`}>
        {badge.text}
      </span>
    );
  };

  const getLevelLabel = (level) => {
    const labels = {
      sympathisant: 'Adhérent Sympathisant',
      participant: 'Adhérent Participant',
      actif: 'Adhérent Actif'
    };
    return labels[level] || level;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 style={{ color: '#00256C' }}>
          <i className="fas fa-tachometer-alt me-2"></i>
          Tableau de Bord Administrateur
        </h2>
        <button 
          className="btn btn-outline-primary"
          onClick={() => fetchAdminData()}
        >
          <i className="fas fa-sync-alt me-2"></i>
          Actualiser
        </button>
      </div>

      {/* Onglets */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
            style={{
              color: activeTab === 'overview' ? '#1E4F8A' : '#6c757d',
              borderBottomColor: activeTab === 'overview' ? '#1E4F8A' : 'transparent'
            }}
          >
            <i className="fas fa-chart-pie me-2"></i>
            Vue d'ensemble
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'sponsorships' ? 'active' : ''}`}
            onClick={() => setActiveTab('sponsorships')}
            style={{
              color: activeTab === 'sponsorships' ? '#1E4F8A' : '#6c757d',
              borderBottomColor: activeTab === 'sponsorships' ? '#1E4F8A' : 'transparent'
            }}
          >
            <i className="fas fa-users me-2"></i>
            Parrainages
            {data?.pending_sponsorships?.length > 0 && (
              <span className="badge bg-warning text-dark ms-1">
                {data.pending_sponsorships.length}
              </span>
            )}
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
            style={{
              color: activeTab === 'users' ? '#1E4F8A' : '#6c757d',
              borderBottomColor: activeTab === 'users' ? '#1E4F8A' : 'transparent'
            }}
          >
            <i className="fas fa-user-friends me-2"></i>
            Utilisateurs
          </button>
        </li>
      </ul>

      {/* Contenu des onglets */}
      <div className="tab-content">
        {/* Vue d'ensemble */}
        {activeTab === 'overview' && (
          <div className="tab-pane fade show active">
            <div className="row">
              {/* Statistiques générales */}
              <div className="col-lg-3 col-md-6 mb-4">
                <div className="card bg-primary text-white">
                  <div className="card-body">
                    <div className="d-flex justify-content-between">
                      <div>
                        <h4>{data?.stats?.total_users || 0}</h4>
                        <p className="mb-0">Utilisateurs Total</p>
                      </div>
                      <div>
                        <i className="fas fa-users fa-2x"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-3 col-md-6 mb-4">
                <div className="card bg-warning text-dark">
                  <div className="card-body">
                    <div className="d-flex justify-content-between">
                      <div>
                        <h4>{data?.stats?.pending_sponsorships || 0}</h4>
                        <p className="mb-0">En Attente</p>
                      </div>
                      <div>
                        <i className="fas fa-clock fa-2x"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-3 col-md-6 mb-4">
                <div className="card bg-success text-white">
                  <div className="card-body">
                    <div className="d-flex justify-content-between">
                      <div>
                        <h4>{data?.stats?.approved_sponsorships || 0}</h4>
                        <p className="mb-0">Approuvés</p>
                      </div>
                      <div>
                        <i className="fas fa-check fa-2x"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-3 col-md-6 mb-4">
                <div className="card bg-info text-white">
                  <div className="card-body">
                    <div className="d-flex justify-content-between">
                      <div>
                        <h4>{data?.stats?.completed_sponsorships || 0}</h4>
                        <p className="mb-0">Complétés</p>
                      </div>
                      <div>
                        <i className="fas fa-trophy fa-2x"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Graphiques et tableaux récents */}
            <div className="row">
              <div className="col-md-8">
                <div className="card">
                  <div className="card-header">
                    <h5 className="mb-0" style={{ color: '#00256C' }}>
                      Parrainages Récents
                    </h5>
                  </div>
                  <div className="card-body">
                    {data?.recent_sponsorships?.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Email Filleul</th>
                              <th>Niveau</th>
                              <th>Initiateur</th>
                              <th>Statut</th>
                              <th>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.recent_sponsorships.map(sponsorship => (
                              <tr key={sponsorship.id}>
                                <td>{sponsorship.filleul_email}</td>
                                <td>{getLevelLabel(sponsorship.requested_level)}</td>
                                <td>{sponsorship.initiator_name}</td>
                                <td>{getStatusBadge(sponsorship.status)}</td>
                                <td>{formatDate(sponsorship.created_at)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-muted">Aucun parrainage récent</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="card">
                  <div className="card-header">
                    <h5 className="mb-0" style={{ color: '#00256C' }}>
                      Répartition par Niveau
                    </h5>
                  </div>
                  <div className="card-body">
                    {data?.level_distribution && (
                      <div>
                        {Object.entries(data.level_distribution).map(([level, count]) => (
                          <div key={level} className="mb-2">
                            <div className="d-flex justify-content-between">
                              <span>{getLevelLabel(level)}</span>
                              <span><strong>{count}</strong></span>
                            </div>
                            <div className="progress" style={{ height: '8px' }}>
                              <div
                                className="progress-bar"
                                role="progressbar"
                                style={{ 
                                  width: `${(count / data.stats.total_users) * 100}%`,
                                  backgroundColor: '#1E4F8A'
                                }}
                              ></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Parrainages */}
        {activeTab === 'sponsorships' && (
          <div className="tab-pane fade show active">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0" style={{ color: '#00256C' }}>
                  Parrainages en Attente de Validation
                </h5>
              </div>
              <div className="card-body">
                {data?.pending_sponsorships?.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Email Filleul</th>
                          <th>Niveau Demandé</th>
                          <th>Initiateur</th>
                          <th>Soutiens</th>
                          <th>Date Création</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.pending_sponsorships.map(sponsorship => (
                          <tr key={sponsorship.id}>
                            <td>
                              <strong>{sponsorship.filleul_email}</strong>
                            </td>
                            <td>
                              {getLevelLabel(sponsorship.requested_level)}
                            </td>
                            <td>
                              {sponsorship.initiator_name}
                            </td>
                            <td>
                              <div className="progress mb-1" style={{ height: '20px' }}>
                                <div
                                  className="progress-bar"
                                  role="progressbar"
                                  style={{ 
                                    width: `${(sponsorship.supporters_count / sponsorship.required_supporters_count) * 100}%`,
                                    backgroundColor: '#1E4F8A'
                                  }}
                                >
                                  {sponsorship.supporters_count} / {sponsorship.required_supporters_count}
                                </div>
                              </div>
                              <small className="text-muted">
                                {sponsorship.quorum_description}
                              </small>
                            </td>
                            <td>
                              {formatDate(sponsorship.created_at)}
                            </td>
                            <td>
                              <div className="btn-group btn-group-sm">
                                <a 
                                  href={`/sponsorships/${sponsorship.id}`}
                                  className="btn btn-outline-primary"
                                  title="Voir détails"
                                >
                                  <i className="fas fa-eye"></i>
                                </a>
                                <button
                                  className="btn btn-outline-success"
                                  onClick={() => handleSponsorshipAction(sponsorship.id, 'approve')}
                                  title="Approuver"
                                >
                                  <i className="fas fa-check"></i>
                                </button>
                                <button
                                  className="btn btn-outline-danger"
                                  onClick={() => {
                                    if (confirm('Êtes-vous sûr de vouloir rejeter ce parrainage ?')) {
                                      handleSponsorshipAction(sponsorship.id, 'reject');
                                    }
                                  }}
                                  title="Rejeter"
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <i className="fas fa-check-circle fa-3x text-success mb-3"></i>
                    <h5 className="text-muted">Aucun parrainage en attente</h5>
                    <p className="text-muted">
                      Tous les parrainages ont été traités.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Utilisateurs */}
        {activeTab === 'users' && (
          <div className="tab-pane fade show active">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0" style={{ color: '#00256C' }}>
                  Gestion des Utilisateurs
                </h5>
                <button
                  className="btn btn-sm"
                  style={{ background: 'linear-gradient(135deg,#7c2837,#a8384e)', color: '#fff', fontWeight: 600 }}
                  onClick={() => { setCreateResult(null); setShowCreateModal(true); }}
                >
                  ＋ Ajouter un membre
                </button>
              </div>
              <div className="card-body">
                {data?.recent_users?.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Nom</th>
                          <th>Email</th>
                          <th>Niveau</th>
                          <th>Rôles</th>
                          <th>Date d'inscription</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.recent_users.map(user => (
                          <tr key={user.id}>
                            <td>
                              <strong>{user.name}</strong>
                            </td>
                            <td>
                              {user.email}
                            </td>
                            <td>
                              <span className="badge bg-info">
                                {getLevelLabel(user.level)}
                              </span>
                            </td>
                            <td>
                              {user.roles && user.roles.length > 0 ? (
                                user.roles.map(role => (
                                  <span key={role} className="badge bg-secondary me-1">
                                    {role}
                                  </span>
                                ))
                              ) : (
                                <span className="text-muted">Aucun rôle</span>
                              )}
                            </td>
                            <td>
                              {formatDate(user.created_at)}
                            </td>
                            <td>
                              <a 
                                href={`/admin/users/${user.id}`}
                                className="btn btn-sm btn-outline-primary"
                              >
                                <i className="fas fa-edit me-1"></i>
                                Modifier
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted">Aucun utilisateur trouvé</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ===== MODAL CRÉER UN MEMBRE ===== */}
      {showCreateModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(44,14,22,0.55)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', padding: '16px'
        }}>
          <div style={{
            background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '500px',
            boxShadow: '0 8px 40px rgba(124,40,55,0.20)', overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg,#7c2837,#a8384e)', padding: '24px 28px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h5 style={{ margin: 0, fontWeight: 700 }}>➕ Ajouter un membre</h5>
              <button onClick={() => setShowCreateModal(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '20px', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            {/* Body */}
            <div style={{ padding: '28px' }}>
              {createResult && (
                <div style={{
                  padding: '12px 16px', borderRadius: '8px', marginBottom: '18px', fontSize: '14px',
                  background: (createResult.success !== false) ? '#f0fff4' : '#fdf0f2',
                  border: `1px solid ${(createResult.success !== false) ? 'rgba(21,128,61,0.25)' : 'rgba(168,56,78,0.25)'}`,
                  color: (createResult.success !== false) ? '#15803d' : '#7c2837'
                }}>
                  {(createResult.success !== false) ? '✅' : '❌'} {createResult.message}
                  {createResult.temporary_password && (
                    <div style={{ marginTop: '8px', color: '#7a4050', fontFamily: 'monospace' }}>
                      Mot de passe temporaire : <strong>{createResult.temporary_password}</strong>
                    </div>
                  )}
                </div>
              )}
              <form onSubmit={handleCreateMember}>
                {[
                  { name: 'first_name', label: 'Prénom *', placeholder: 'Jean', type: 'text' },
                  { name: 'last_name',  label: 'Nom *',    placeholder: 'Dupont', type: 'text' },
                  { name: 'email',      label: 'Email *',  placeholder: 'jean.dupont@exemple.fr', type: 'email' },
                ].map(field => (
                  <div key={field.name} style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#7a4050', marginBottom: '5px' }}>{field.label}</label>
                    <input
                      type={field.type}
                      value={createForm[field.name]}
                      onChange={e => setCreateForm(f => ({ ...f, [field.name]: e.target.value }))}
                      placeholder={field.placeholder}
                      required
                      style={{ width: '100%', padding: '10px 13px', border: '1.5px solid rgba(124,40,55,0.18)', borderRadius: '8px', background: '#fdf6f8', color: '#2c0e16', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>
                ))}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#7a4050', marginBottom: '5px' }}>Niveau</label>
                    <select value={createForm.level} onChange={e => setCreateForm(f => ({ ...f, level: e.target.value }))}
                      style={{ width: '100%', padding: '10px 13px', border: '1.5px solid rgba(124,40,55,0.18)', borderRadius: '8px', background: '#fdf6f8', color: '#2c0e16', fontSize: '14px' }}>
                      <option value="invite">Invité</option>
                      <option value="adherent_sympathisant">Sympathisant</option>
                      <option value="adherent_participant">Participant</option>
                      <option value="adherent_actif">Actif</option>
                      <option value="membre_fondateur">Fondateur</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#7a4050', marginBottom: '5px' }}>Rôle</label>
                    <select value={createForm.role} onChange={e => setCreateForm(f => ({ ...f, role: e.target.value }))}
                      style={{ width: '100%', padding: '10px 13px', border: '1.5px solid rgba(124,40,55,0.18)', borderRadius: '8px', background: '#fdf6f8', color: '#2c0e16', fontSize: '14px' }}>
                      <option value="aucun_role">Aucun rôle</option>
                      <option value="president">Président(e)</option>
                      <option value="vice_president">Vice-président(e)</option>
                      <option value="secretaire">Secrétaire</option>
                      <option value="tresorier">Trésorier(e)</option>
                      <option value="membre_ca">Membre CA</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button type="button" onClick={() => setShowCreateModal(false)}
                    style={{ flex: 1, padding: '12px', background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.2)', color: '#2c0e16', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
                    Annuler
                  </button>
                  <button type="submit" disabled={createLoading}
                    style={{ flex: 1, padding: '12px', background: createLoading ? '#cca7b0' : 'linear-gradient(135deg,#7c2837,#a8384e)', color: '#fff', border: 'none', borderRadius: '8px', cursor: createLoading ? 'not-allowed' : 'pointer', fontWeight: 700 }}>
                    {createLoading ? 'Création…' : 'Créer le membre'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;